import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthChange, logOut } from './firebase/auth';
import { isFirebaseConfigured } from './firebase/config';
import ErrorBoundary from './components/common/ErrorBoundary';

// Lazy load components for performance
const LandingPage = React.lazy(() => import('./components/Landing/LandingPage'));
const Login = React.lazy(() => import('./components/Auth/Login'));
const SignUp = React.lazy(() => import('./components/Auth/SignUp'));
const ProtectedRoute = React.lazy(() => import('./components/Auth/ProtectedRoute'));
const AdminRoute = React.lazy(() => import('./components/Auth/AdminRoute'));
const Dashboard = React.lazy(() => import('./components/Dashboard/Dashboard'));
const Settings = React.lazy(() => import('./components/Settings/Settings'));
const PrivacyPolicy = React.lazy(() => import('./components/Privacy/PrivacyPolicy'));
const CoachDashboard = React.lazy(() => import('./components/Coach/CoachDashboard'));
const CoachSetup = React.lazy(() => import('./components/Coach/CoachSetup'));
const CoachHistory = React.lazy(() => import('./components/Coach/CoachHistory'));
const LearningTimeline = React.lazy(() => import('./components/Student/LearningTimeline'));
const PersistentChatPage = React.lazy(() => import('./components/Chatbot/PersistentChatPage'));
const SessionMetrics = React.lazy(() => import('./components/SessionMetrics/SessionMetrics'));
const WeeklyInsights = React.lazy(() => import('./components/WeeklyInsights/WeeklyInsights'));
const RunnerDashboard = React.lazy(() => import('./components/Runner/RunnerDashboard'));
const PostRunReflection = React.lazy(() => import('./components/Runner/PostRunReflection'));
const RunnerOnboardingFlow = React.lazy(() => import('./components/Onboarding/RunnerOnboardingFlow'));
const DataMigrationTool = React.lazy(() => import('./components/Admin/DataMigrationTool'));
const InviteManager = React.lazy(() => import('./components/Admin/InviteManager'));
const AdminDashboard = React.lazy(() => import('./components/Admin/AdminDashboard'));
const RetroactiveExtractionTool = React.lazy(() => import('./components/Admin/RetroactiveExtractionTool'));
const UserProfile = React.lazy(() => import('./components/Admin/UserProfile'));
const ScheduleTester = React.lazy(() => import('./components/Admin/ScheduleTester'));
const MassMessaging = React.lazy(() => import('./components/Admin/MassMessaging'));
const CampaignManager = React.lazy(() => import('./components/Admin/CampaignManager'));
const KairoDashboard = React.lazy(() => import('./components/Kairo/KairoDashboard'));
const InviteLanding = React.lazy(() => import('./components/Onboarding/InviteLanding'));
const EnduranceLandingPage = React.lazy(() => import('./components/Landing/EnduranceLandingPage'));
const PricingPage = React.lazy(() => import('./components/Pricing/PricingPage'));
const TechniquePage = React.lazy(() => import('./pages/TechniquePage'));
const HolidayCampaign = React.lazy(() => import('./pages/HolidayCampaign'));
const RedeemGiftCard = React.lazy(() => import('./pages/RedeemGiftCard'));
const BadgesPage = React.lazy(() => import('./pages/BadgesPage'));
const VideoUpload = React.lazy(() => import('./pages/VideoUpload'));
const GameTapeReview = React.lazy(() => import('./pages/GameTapeReview'));
const NotesImport = React.lazy(() => import('./pages/NotesImport'));

// Loading Fallback
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSignUp, setShowSignUp] = useState(false);

  useEffect(() => {
    if (!isFirebaseConfigured) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthChange((currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await logOut();
    setUser(null);
  };

  if (!isFirebaseConfigured) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Configuration Required
            </h1>
            <p className="text-gray-600 mb-6">
              Firebase environment variables are not configured. Please set up the following environment variables in Vercel:
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <code className="text-sm text-gray-800">
                <div>VITE_FIREBASE_API_KEY</div>
                <div>VITE_FIREBASE_AUTH_DOMAIN</div>
                <div>VITE_FIREBASE_PROJECT_ID</div>
                <div>VITE_FIREBASE_STORAGE_BUCKET</div>
                <div>VITE_FIREBASE_MESSAGING_SENDER_ID</div>
                <div>VITE_FIREBASE_APP_ID</div>
                <div>VITE_FIREBASE_MEASUREMENT_ID</div>
                <div className="mt-4 pt-4 border-t border-gray-300">VITE_VERTEX_AI_API_KEY (optional)</div>
              </code>
            </div>
            <p className="text-sm text-gray-500">
              Go to your Vercel project settings → Environment Variables to add these values.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <ErrorBoundary>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route
                path="/login"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-gray-100 flex items-center justify-center px-4 py-12">
                      <div className="w-full max-w-6xl">
                        {showSignUp ? (
                          <SignUp onSwitchToLogin={() => setShowSignUp(false)} />
                        ) : (
                          <Login onSwitchToSignUp={() => setShowSignUp(true)} />
                        )}
                      </div>
                    </div>
                  )
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard user={user} onLogout={handleLogout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings user={user} />
                  </ProtectedRoute>
                }
              />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/holiday" element={<HolidayCampaign />} />
              <Route path="/redeem" element={<RedeemGiftCard />} />
              <Route path="/upload/:token" element={<VideoUpload />} />
              <Route path="/game-tape/:docId" element={<GameTapeReview />} />
              <Route
                path="/import-notes"
                element={
                  <ProtectedRoute user={user}>
                    <NotesImport />
                  </ProtectedRoute>
                }
              />
              <Route path="/import-notes/:token" element={<NotesImport />} />
              <Route path="/kairo" element={<KairoDashboard />} />
              <Route path="/technique/:slug" element={<TechniquePage />} />
              <Route
                path="/coach/dashboard"
                element={
                  <ProtectedRoute>
                    <CoachDashboard user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coach/setup"
                element={
                  <ProtectedRoute>
                    <CoachSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coach/history"
                element={
                  <ProtectedRoute>
                    <CoachHistory user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/timeline"
                element={
                  <ProtectedRoute>
                    <LearningTimeline user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chatbot"
                element={
                  <ProtectedRoute>
                    <PersistentChatPage />
                  </ProtectedRoute>
                }
              />
              {/* Redirect /chat to /chatbot for convenience */}
              <Route
                path="/chat"
                element={<Navigate to="/chatbot" replace />}
              />
              <Route
                path="/metrics"
                element={
                  <ProtectedRoute>
                    <SessionMetrics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/weekly-insights"
                element={
                  <ProtectedRoute>
                    <WeeklyInsights />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/migrate"
                element={
                  <AdminRoute>
                    <DataMigrationTool />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/retroactive"
                element={
                  <AdminRoute>
                    <RetroactiveExtractionTool />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <AdminRoute>
                    <UserProfile />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/invite"
                element={
                  <AdminRoute>
                    <InviteManager />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/schedule-test"
                element={
                  <AdminRoute>
                    <ScheduleTester />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/campaigns"
                element={
                  <AdminRoute>
                    <CampaignManager />
                  </AdminRoute>
                }
              />
              <Route path="/invite" element={<InviteLanding />} />
              {/* Runner-specific routes */}
              <Route
                path="/run/dashboard"
                element={
                  <ProtectedRoute>
                    <RunnerDashboard user={user} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/run/reflection"
                element={
                  <ProtectedRoute>
                    <PostRunReflection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/run/reflection/:reflectionId"
                element={
                  <ProtectedRoute>
                    <PostRunReflection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/run/strava/callback"
                element={
                  <ProtectedRoute>
                    <RunnerDashboard user={user} />
                  </ProtectedRoute>
                }
              />
              {/* Hidden endurance sport routes - not linked from main BJJ site */}
              <Route path="/run" element={<EnduranceLandingPage user={user} />} />
              <Route path="/cycle" element={<EnduranceLandingPage user={user} />} />
              <Route path="/" element={<LandingPage user={user} />} />
              <Route path="/signup" element={
                user ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-gray-100 flex items-center justify-center px-4 py-12">
                    <div className="w-full max-w-6xl">
                      <SignUp onSwitchToLogin={() => setShowSignUp(false)} />
                    </div>
                  </div>
                )
              } />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
